import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import * as L from 'leaflet';
import { DecimalPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ProjectsService } from '@cosmos/angular/api';
import { ErrorService } from '@cosmos/angular/utils';
import { Project } from '@cosmos/shared';

// Fix default marker icon paths broken by Angular's asset bundling
const DEFAULT_ICON = L.icon({
  iconUrl: 'assets/leaflet/marker-icon.png',
  shadowUrl: 'assets/leaflet/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [13, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

@Component({
  selector: 'cosmos-project-location-settings',
  templateUrl: './project-location-settings.component.html',
  styleUrl: './project-location-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DecimalPipe, TranslatePipe, ButtonModule],
})
export class ProjectLocationSettingsComponent implements AfterViewInit, OnDestroy {
  private readonly projectsService = inject(ProjectsService);
  private readonly errorService = inject(ErrorService);

  readonly project = input.required<Project>();

  readonly updatedProject = output<Project>();

  readonly saving = signal(false);
  readonly saved = signal(false);

  /** [lon, lat] — GeoJSON order, set when user clicks the map */
  readonly pendingCoordinates = signal<[number, number] | null>(null);

  @ViewChild('mapContainer', { static: true })
  private mapContainer!: ElementRef<HTMLDivElement>;

  private map!: L.Map;
  private marker: L.Marker | null = null;

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    this.map?.remove();
  }

  private initMap(): void {
    const existingLoc = this.project().location;
    const hasLocation = !!(existingLoc?.coordinates?.[0] !== undefined);

    const initialView: L.LatLngExpression = hasLocation
      ? [existingLoc.coordinates[1], existingLoc.coordinates[0]]
      : [40.416775, -3.70379]; // default: Madrid
    const initialZoom = hasLocation ? 13 : 5;

    this.map = L.map(this.mapContainer.nativeElement).setView(initialView, initialZoom);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      className: 'map-tiles'
    }).addTo(this.map);

    if (hasLocation) {
      this.marker = L.marker(initialView as L.LatLngExpression, { icon: DEFAULT_ICON }).addTo(this.map);
      this.pendingCoordinates.set([existingLoc.coordinates[0], existingLoc.coordinates[1]]);
    }

    this.map.on('click', (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      if (this.marker) {
        this.marker.setLatLng([lat, lng]);
      } else {
        this.marker = L.marker([lat, lng], { icon: DEFAULT_ICON }).addTo(this.map);
      }
      // GeoJSON stores [lon, lat]
      this.pendingCoordinates.set([lng, lat]);
      this.saved.set(false);
    });
  }

  save(): void {
    const coords = this.pendingCoordinates();
    if (!coords || this.saving()) return;

    this.saving.set(true);
    this.projectsService
      .update([this.project()._id, { location: { type: 'Point', coordinates: coords } }])
      .subscribe({
        next: res => {
          this.saving.set(false);
          this.saved.set(true);
          this.updatedProject.emit(res.body);
        },
        error: err => {
          this.saving.set(false);
          this.errorService.handleError(err);
        },
      });
  }
}
